import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { AppBar } from '@/components/AppBar';
import { PageContainer, PageInner } from '@/components/ui/PageContainer';
import { PageError, PageSkeleton } from '@/components/Skeleton';
import { HistoryStats } from '@/components/HistoryStats';
import { HistoryFilters, type MondaiFilter } from '@/components/HistoryFilters';
import { HistoryAttemptCard } from '@/components/HistoryAttemptCard';
import { HistoryProgressCard } from '@/components/HistoryProgressCard';
import { useAuth } from '@/lib/AuthProvider';
import { listAttempts, type AttemptDoc } from '@/lib/attempts';
import { listProgress, type CloudProgress } from '@/lib/userProgress';
import { getCoursesList } from '@/lib/queries';
import {
  enrichProgress,
  filterByMondai,
  groupAttemptsBySection,
  summarizeStats,
  type AttemptGroup,
  type ProgressEntry,
} from '@/lib/history';
import type { CourseDoc } from '@/lib/types';

export const Route = createFileRoute('/history')({
  component: HistoryScreen,
});

function HistoryScreen() {
  const navigate = useNavigate();
  const { user, isReady } = useAuth();
  const uid = user?.uid ?? null;

  const [attempts, setAttempts] = useState<AttemptDoc[]>([]);
  const [progress, setProgress] = useState<CloudProgress[]>([]);
  const [courses, setCourses] = useState<CourseDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [filter, setFilter] = useState<MondaiFilter>('all');

  // Redirect guests to /login
  useEffect(() => {
    if (isReady && !uid) {
      navigate({ to: '/login' });
    }
  }, [isReady, uid, navigate]);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([listAttempts(uid), listProgress(uid), getCoursesList()])
      .then(([a, p, c]) => {
        if (cancelled) return;
        setAttempts(a);
        setProgress(p);
        setCourses(c);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const coursesBySlug = useMemo(() => {
    const m = new Map<string, CourseDoc>();
    for (const c of courses) m.set(c.slug, c);
    return m;
  }, [courses]);

  const allGroups: AttemptGroup[] = useMemo(
    () => groupAttemptsBySection(attempts, coursesBySlug),
    [attempts, coursesBySlug],
  );
  const allProgress: ProgressEntry[] = useMemo(
    () => enrichProgress(progress, coursesBySlug),
    [progress, coursesBySlug],
  );

  const summary = useMemo(() => summarizeStats(attempts), [attempts]);

  const filteredGroups = useMemo(() => filterByMondai(allGroups, filter), [allGroups, filter]);
  const filteredProgress = useMemo(() => filterByMondai(allProgress, filter), [allProgress, filter]);

  if (!isReady || (isReady && !uid)) {
    return <PageSkeleton />;
  }

  return (
    <PageContainer>
      <AppBar title="Lịch sử học" onBack={() => navigate({ to: '/' })} />
      <PageInner className="pt-5">
        {loading ? (
          <PageSkeleton />
        ) : error ? (
          <PageError error={error} onRetry={() => window.location.reload()} />
        ) : (
          <div className="flex flex-col gap-4">
            <HistoryStats summary={summary} />

            <HistoryFilters value={filter} onChange={setFilter} />

            {filteredProgress.length > 0 && (
              <section className="flex flex-col gap-2">
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-ink-3">
                  Đang làm ({filteredProgress.length})
                </h2>
                {filteredProgress.map((entry) => (
                  <HistoryProgressCard
                    key={`${entry.courseSlug}__${entry.sectionSlug}`}
                    entry={entry}
                  />
                ))}
              </section>
            )}

            {filteredGroups.length > 0 ? (
              <section className="flex flex-col gap-2">
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-ink-3">
                  Đã nộp ({filteredGroups.length})
                </h2>
                {filteredGroups.map((g) => (
                  <HistoryAttemptCard key={`${g.courseSlug}__${g.sectionSlug}`} group={g} />
                ))}
              </section>
            ) : (
              filteredProgress.length === 0 && (
                <EmptyState onStart={() => navigate({ to: '/' })} hasFilter={filter !== 'all'} />
              )
            )}
          </div>
        )}
      </PageInner>
    </PageContainer>
  );
}

function EmptyState({ onStart, hasFilter }: { onStart: () => void; hasFilter: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border bg-surface px-6 py-10 text-center">
      <span className="text-[36px]">📚</span>
      <div className="text-[15px] font-bold text-ink-1">
        {hasFilter ? 'Không có bài nào trong bộ lọc này' : 'Bạn chưa làm bài nào'}
      </div>
      <p className="max-w-xs text-[12px] text-ink-2">
        {hasFilter
          ? 'Hãy thử đổi bộ lọc hoặc bắt đầu làm thêm mondai khác.'
          : 'Bắt đầu luyện nghe để xem lịch sử và tiến độ của bạn.'}
      </p>
      <button
        type="button"
        onClick={onStart}
        className="mt-2 rounded-xl bg-[var(--brand)] px-5 py-2 text-[13px] font-bold text-white"
      >
        Bắt đầu học →
      </button>
    </div>
  );
}
